import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignsAPI, type Campaign, type CampaignObjective, type CreateCampaignInput } from '../services/api';

// Available metrics for objectives
const AVAILABLE_METRICS = [
  { value: 'interactions_logged', label: 'Interactions Logged' },
  { value: 'contacts_added', label: 'Contacts Added' },
  { value: 'deals_closed', label: 'Deals Closed' },
  { value: 'calls_made', label: 'Calls Made' },
  { value: 'meetings_held', label: 'Meetings Held' },
  { value: 'emails_sent', label: 'Emails Sent' },
];

export default function Campaigns() {
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['campaigns', filter],
    queryFn: () => campaignsAPI.list(filter),
  });

  const campaigns = data?.data?.campaigns || [];

  const joinMutation = useMutation({
    mutationFn: campaignsAPI.join,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const leaveMutation = useMutation({
    mutationFn: campaignsAPI.leave,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: campaignsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ondeck-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Campaigns</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
            Create and track team competitions
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-3 sm:px-4 py-2 bg-ondeck-600 text-white rounded-lg hover:bg-ondeck-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="hidden sm:inline">New Campaign</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {(['all', 'active', 'completed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filter === f
                ? 'bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-700 dark:text-ondeck-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Campaign List */}
      {campaigns.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
          <span className="text-4xl mb-4 block">🏆</span>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Campaigns Yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Create your first campaign to start tracking team progress!
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-ondeck-600 text-white rounded-lg hover:bg-ondeck-700 transition-colors"
          >
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onView={() => setSelectedCampaign(campaign)}
              onJoin={() => joinMutation.mutate(campaign.id)}
              onLeave={() => leaveMutation.mutate(campaign.id)}
              onDelete={() => {
                if (confirm('Are you sure you want to delete this campaign?')) {
                  deleteMutation.mutate(campaign.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <CreateCampaignModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
          }}
        />
      )}

      {/* Campaign Detail Modal */}
      {selectedCampaign && (
        <CampaignDetailModal
          campaignId={selectedCampaign.id}
          onClose={() => setSelectedCampaign(null)}
        />
      )}
    </div>
  );
}

// ============================================
// Campaign Card Component
// ============================================

function CampaignCard({
  campaign,
  onView,
  onJoin,
  onLeave,
  onDelete,
}: {
  campaign: Campaign;
  onView: () => void;
  onJoin: () => void;
  onLeave: () => void;
  onDelete: () => void;
}) {
  const now = new Date();
  const start = new Date(campaign.start_date);
  const end = new Date(campaign.end_date);

  const isActive = campaign.is_active && end >= now;
  const isUpcoming = start > now;
  const daysLeft = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{campaign.name}</h3>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
              isUpcoming
                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400'
                : isActive
                ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}>
              {isUpcoming ? 'Upcoming' : isActive ? 'Active' : 'Completed'}
            </span>
            {campaign.is_team_goal ? (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400">
                Team Goal
              </span>
            ) : null}
          </div>

          {campaign.description && (
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">{campaign.description}</p>
          )}

          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {campaign.participant_count || 0} participants
            </span>
            {isActive && daysLeft > 0 && (
              <span className="flex items-center gap-1 text-ondeck-600 dark:text-ondeck-400 font-medium">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {daysLeft} days left
              </span>
            )}
          </div>

          {/* Objectives Preview */}
          <div className="mt-3 flex flex-wrap gap-2">
            {campaign.objectives.slice(0, 3).map((obj, i) => (
              <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded">
                {obj.label || `${obj.metric}: ${obj.target}`}
              </span>
            ))}
            {campaign.objectives.length > 3 && (
              <span className="px-2 py-1 text-gray-500 dark:text-gray-400 text-xs">
                +{campaign.objectives.length - 3} more
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={onView}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="View Details"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>

          {isActive && !campaign.is_participating && (
            <button
              onClick={onJoin}
              className="px-3 py-1.5 bg-ondeck-600 text-white text-sm rounded-lg hover:bg-ondeck-700 transition-colors"
            >
              Join
            </button>
          )}

          {isActive && campaign.is_participating && (
            <button
              onClick={onLeave}
              className="px-3 py-1.5 text-gray-600 dark:text-gray-400 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Leave
            </button>
          )}

          <button
            onClick={onDelete}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Create Campaign Modal
// ============================================

function CreateCampaignModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isTeamGoal, setIsTeamGoal] = useState(false);
  const [objectives, setObjectives] = useState<CampaignObjective[]>([
    { metric: 'interactions_logged', target: 50, label: 'Log 50 interactions' },
  ]);
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: campaignsAPI.create,
    onSuccess: (data) => {
      if (data.success) {
        onSuccess();
      } else {
        setError(data.error || 'Failed to create campaign');
      }
    },
    onError: () => {
      setError('Failed to create campaign');
    },
  });

  const addObjective = () => {
    setObjectives([...objectives, { metric: 'interactions_logged', target: 10, label: '' }]);
  };

  const removeObjective = (index: number) => {
    setObjectives(objectives.filter((_, i) => i !== index));
  };

  const updateObjective = (index: number, field: keyof CampaignObjective, value: string | number) => {
    const updated = [...objectives];
    updated[index] = { ...updated[index], [field]: value };
    // Auto-generate label if not custom
    if (field === 'metric' || field === 'target') {
      const metricLabel = AVAILABLE_METRICS.find(m => m.value === updated[index].metric)?.label || updated[index].metric;
      updated[index].label = `${metricLabel}: ${updated[index].target}`;
    }
    setObjectives(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name || !startDate || !endDate || objectives.length === 0) {
      setError('Please fill in all required fields');
      return;
    }

    const input: CreateCampaignInput = {
      name,
      description: description || undefined,
      start_date: startDate,
      end_date: endDate,
      objectives,
      is_team_goal: isTeamGoal,
    };

    createMutation.mutate(input);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create Campaign</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Campaign Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q1 Push - New Listings"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the campaign goals..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
              />
            </div>
          </div>

          {/* Team Goal Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isTeamGoal"
              checked={isTeamGoal}
              onChange={(e) => setIsTeamGoal(e.target.checked)}
              className="w-4 h-4 text-ondeck-600 border-gray-300 rounded focus:ring-ondeck-500"
            />
            <label htmlFor="isTeamGoal" className="text-sm text-gray-700 dark:text-gray-300">
              Team Goal (everyone works together instead of competing)
            </label>
          </div>

          {/* Objectives */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Objectives *
              </label>
              <button
                type="button"
                onClick={addObjective}
                className="text-sm text-ondeck-600 hover:text-ondeck-700 dark:text-ondeck-400"
              >
                + Add Objective
              </button>
            </div>
            <div className="space-y-3">
              {objectives.map((obj, index) => (
                <div key={index} className="flex items-center gap-2">
                  <select
                    value={obj.metric}
                    onChange={(e) => updateObjective(index, 'metric', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
                  >
                    {AVAILABLE_METRICS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={obj.target}
                    onChange={(e) => updateObjective(index, 'target', parseInt(e.target.value) || 0)}
                    min={1}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
                  />
                  {objectives.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeObjective(index)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-ondeck-600 text-white rounded-lg hover:bg-ondeck-700 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// Campaign Detail Modal
// ============================================

function CampaignDetailModal({
  campaignId,
  onClose,
}: {
  campaignId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => campaignsAPI.get(campaignId),
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ondeck-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  const campaign = data?.data?.campaign;
  const participants = data?.data?.participants || [];

  if (!campaign) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{campaign.name}</h2>
              {campaign.description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">{campaign.description}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Objectives */}
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Objectives</h3>
            <div className="grid gap-2">
              {campaign.objectives.map((obj, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <span className="text-gray-700 dark:text-gray-300">{obj.label}</span>
                  <span className="font-medium text-ondeck-600 dark:text-ondeck-400">Target: {obj.target}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Leaderboard */}
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Leaderboard</h3>
            {participants.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">No participants yet</p>
            ) : (
              <div className="space-y-2">
                {participants.map((p, i) => (
                  <div
                    key={p.user_id}
                    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                      i === 0 ? 'bg-yellow-100 text-yellow-700' :
                      i === 1 ? 'bg-gray-200 text-gray-600' :
                      i === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                    }`}>
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{p.user_name}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-32 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-ondeck-500 h-2 rounded-full"
                          style={{ width: `${Math.min(p.progress_percent, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-12 text-right">
                        {p.progress_percent}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
